/**
 * Suggested question item view component
 * @remarks
 * Displays a single suggested question as a pressable chip with icon and text.
 * Styling is expressed entirely through closed design-system tokens. The
 * surface/border/content colors are theme-aware semantic tokens that swap
 * light/dark automatically via the Gluestack provider's CSS vars, so no
 * runtime `isDark` style prop is required (SE-5380 — replaces the prior
 * bespoke isDark-keyed hex palette). The responsive two-up vs full-width
 * layout is gated on screen width via the `xl:` breakpoint (the project-wide
 * 1280px desktop split) rather than a measured `windowWidth` style prop.
 */

import { Box } from "@/components/atoms/Box";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

import { TEST_ID_GEMINI_CHAT } from "e2e/testId";

/**
 * Suggested question data structure
 */
interface SuggestedQuestion {
  icon: string;
  text: string;
  query: string;
}

/**
 * Props for SuggestedQuestionItemView component
 */
interface SuggestedQuestionItemViewProps {
  question: SuggestedQuestion;
  onPress: () => void;
}

/**
 * View component for suggested question item
 * @remarks SE-6900: all four chips share one `testID`, so a positional query
 * cannot tell them apart and a test that clicks "the chip" passes even when the
 * wrong chip's text is dispatched. Each chip now carries an accessibility label
 * holding its own question text — a per-chip identity for assistive tech and
 * the anchor tests use to address one specific chip. The shared `testID` is
 * kept as the "all chips" handle the e2e suite counts with.
 * @param root0 - Component props
 * @param root0.question - Suggested question data with icon, text, and query
 * @param root0.onPress - Callback when question item is pressed
 */
const SuggestedQuestionItemView = ({
  question,
  onPress,
}: SuggestedQuestionItemViewProps) => (
  <Pressable
    testID={TEST_ID_GEMINI_CHAT.SUGGESTED_QUESTION}
    accessibilityLabel={question.text}
    accessibilityRole="button"
    // SE-8864: two-up is a third-width basis that grows, not `w-1/2`. The row
    // spaces cards with a gap, so two halves plus the gap overflowed and
    // wrapped one per line. A third leaves room for the gap: two fit and grow
    // to share the row, a third cannot and wraps.
    className="min-h-14 w-full rounded-md border border-outline-subtle bg-surface-raised p-4 xl:grow xl:basis-1/3"
    onPress={onPress}
  >
    <Box className="flex-row items-start gap-3">
      <Text variant="unstyled" className="text-title">
        {question.icon}
      </Text>
      <Text
        variant="unstyled"
        className="flex-1 font-medium text-title text-content-primary"
      >
        {question.text}
      </Text>
    </Box>
  </Pressable>
);

SuggestedQuestionItemView.displayName = "SuggestedQuestionItemView";

export { SuggestedQuestionItemView };
